<!--
  ChequeDepositModal: depósito de uno o varios cheques en cartera en una cuenta bancaria.

  ENTRADA
    Props: no tiene.
    Métodos públicos (por ref):
      open(cheques)   Abre el depósito con esos cheques (array de items del listado).
      loadCash()      Carga las cuentas disponibles. La vista lo llama al montarse y al reactivarse.

  SALIDA
    Eventos:
      @saved(response)   Se registró. La vista limpia la selección y recarga lo que corresponda.
    Efectos:
      Guarda con depositarCheques. EL DEPÓSITO QUEDA PENDIENTE DE ACREDITACIÓN y cambia el estado de los cheques.

  USOS
    Vista: <ChequeDepositModal ref="chequeDeposit" @saved="onDepositSaved()" />
    Abrir: this.$refs.chequeDeposit.open(cheques)
    Hoy se invoca desde finances/staff/cheques.vue: selección, listado y drawer.

  DEPENDENCIAS Y PERMISOS
    Parámetro P61 (freeAddTypeOperation): permite mezclar cheques y cuentas declaradas y no declaradas.
    Acreditar, anular y revertir siguen en inc/chequesDeposits.
    No controla permisos: quien lo invoca decide si el usuario puede abrirlo.
-->
<template>
  <div>
    <!-- DEPOSITAR -->
    <b-modal v-model="modal.deposit.active"
            size="lg"
            :key="'deposit-' + modal.deposit._id">
      <template #modal-header><div class="app-modal-header"><span class="app-modal-header__icon"><AppIcon name="account_balance" /></span><h5 class="app-modal-header__title">Depositar cheques</h5></div></template>

      <b-alert show variant="warning" v-if="depositMixedNd">
        Hay cheques declarados y no declarados en la selección. Deben depositarse en cuentas con la misma condición: separalos en depósitos distintos o modificá la condición del cheque.
      </b-alert>

      <b-row>
        <b-col md="6">
          <b-form-group label="Cuenta bancaria" :description="bankAccounts.length ? '' : 'No hay cuentas bancarias disponibles con la misma condición (D/ND). Configurá el tipo de caja/cuenta en Finanzas > Configuración.'">
            <b-form-select size="sm" v-model="crud.deposit.finances_cash_id" :options="bankAccounts" value-field="id" text-field="name">
              <template #first><b-form-select-option :value="null" disabled>— Seleccionar cuenta —</b-form-select-option></template>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Fecha de depósito">
            <b-form-input type="date" size="sm" v-model="crud.deposit.date" />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group label="Observaciones">
            <app-textarea v-model="crud.deposit.observations" placeholder="N.º de boleta, sucursal, etc." rows="2" max-rows="4"></app-textarea>
          </b-form-group>
        </b-col>
      </b-row>

      <b-table-simple small responsive class="app-table mb-0">
        <b-thead>
          <b-tr>
            <b-th>Cheque</b-th>
            <b-th>Emisor</b-th>
            <b-th>Vencimiento</b-th>
            <b-th class="text-right">Importe</b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr v-for="cheque in depositCheques" :key="cheque.id">
            <b-td><strong>{{ cheque.bank }}</strong> N.º {{ cheque.number }} <b-badge v-if="cheque.nd" variant="secondary">ND</b-badge></b-td>
            <b-td>{{ cheque.transmitter_name }}</b-td>
            <b-td>
              {{ moment(cheque.date_expiration).format('DD/MM/YYYY') }}
              <b-badge v-if="crud.deposit.date && cheque.date_expiration > crud.deposit.date" variant="warning" v-b-tooltip.hover title="La fecha de vencimiento del cheque es posterior a la fecha de depósito">Diferido</b-badge>
            </b-td>
            <b-td class="text-right">{{ Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS'}).format(cheque.amount) }}</b-td>
          </b-tr>
          <b-tr>
            <b-td colspan="3" class="text-right font-weight-bold">Total</b-td>
            <b-td class="text-right font-weight-bold">{{ Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS'}).format(depositTotal) }}</b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>

      <div slot="modal-footer">
        <b-button variant="outline-secondary" class="mr-1" @click="modal.deposit.active=false"><AppIcon name="x" class="mr-1" /> Cancelar</b-button>
        <b-button variant="dark" :disabled="depositMixedNd" @click="saveDeposit()"><AppIcon name="save" class="mr-1" /> Depositar</b-button>
      </div>
    </b-modal>
  </div>
</template>
<script>
  import serviceAPI from '../services'
  import Error from '@/handler/error'
  import Helper from '@/handler/helper'
  import moment from 'moment'
  import Param from '@/config/parameters'

  export default {
    name: 'ChequeDepositModal',
    data: () => {
      return {
        crud: {
          deposit: {
            finances_cash_id: null,
            date: '',
            observations: '',
          },
        },
        modal: {
          deposit: {
            active: false,
          },
        },
        arr: {
          cash: [],
        },
        depositCheques: [],
        parameters: {
          freeAddTypeOperation: false,
        },
      }
    },
    mounted() {
      this.parameters.freeAddTypeOperation = Helper.hasParametersAccess(Param.P61)
    },
    computed: {
      depositTotal() {
        return this.depositCheques.reduce((total, cheque) => total + parseFloat(cheque.amount), 0)
      },
      depositMixedNd() {
        if(this.parameters.freeAddTypeOperation) return false
        return new Set(this.depositCheques.map(cheque => !!cheque.nd)).size > 1
      },
      // cuentas bancarias activas con la misma condición D/ND que los cheques (salvo P61)
      bankAccounts() {
        const nd = this.depositCheques.length ? !!this.depositCheques[0].nd : false
        return this.arr.cash.filter(cash => cash.kind == 'BANK' && cash.active && (this.parameters.freeAddTypeOperation || !!cash.nd == nd))
      },
    },
    methods: {
      loadCash() {
        serviceAPI.obtenerCajas()
        .then((response) => {
          this.arr.cash = response.data
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      open(cheques) {
        this.depositCheques = cheques
        this.crud.deposit.date = moment().format('YYYY-MM-DD')
        this.crud.deposit.observations = ''
        this.crud.deposit.finances_cash_id = this.bankAccounts.length == 1 ? this.bankAccounts[0].id : null

        this.modal.deposit._id = Math.random().toString(36).slice(2, 8)
        this.modal.deposit.active = true
      },
      saveDeposit() {
        let loader = this.$loading.show()
        serviceAPI.depositarCheques({
          finances_cash_id: this.crud.deposit.finances_cash_id,
          date: this.crud.deposit.date,
          observations: this.crud.deposit.observations,
          cheques: this.depositCheques.map(cheque => cheque.id),
        })
        .then((response) => {
          loader.hide()
          this.modal.deposit.active = false
          this.$emit('saved', response)
          this.$awn.success("Depósito registrado, pendiente de acreditación")
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
    },
  }
</script>
